import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, FileText, Download, AlertTriangle, CheckCircle2, RefreshCw, Image as ImageIcon } from "lucide-react";
import { API } from "../constants";

/**
 * Admin: Bulk Catalog CSV Update.
 * - Download starter template (every active product with editable columns)
 * - Pick a CSV → dry-run preview → apply
 * SKU is the update key. Only editable fields overwrite; blanks are ignored.
 */
const CatalogCsvTab = ({ token }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/admin/catalog/csv-template`, {
        responseType: "blob", ...authHeaders,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cutabove-catalog-template.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Template downloaded — edit and re-upload here");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Template download failed");
    }
  };

  const runPreview = async (chosenFile) => {
    const f = chosenFile || file;
    if (!f) { toast.error("Pick a CSV first"); return; }
    setBusy(true); setApplied(null);
    const form = new FormData();
    form.append("file", f);
    try {
      const res = await axios.post(`${API}/admin/catalog/csv-preview`, form, authHeaders);
      setPreview(res.data);
      toast.success(`Parsed ${res.data.parsed_rows} rows · ${res.data.matched} matched · ${res.data.not_found} not found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    }
    setBusy(false);
  };

  const runApply = async () => {
    if (!file) { toast.error("Pick a CSV first"); return; }
    if (!window.confirm(`Apply ${preview?.matched ?? '?'} product update${preview?.matched === 1 ? '' : 's'} now?`)) return;
    setBusy(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const res = await axios.post(`${API}/admin/catalog/csv-apply`, form, authHeaders);
      setApplied(res.data);
      setPreview(res.data);
      toast.success(`Applied — ${res.data.updated} products updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    }
    setBusy(false);
  };

  const onFileChosen = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(null); setApplied(null);
    runPreview(f);
  };

  return (
    <div data-testid="catalog-csv-tab" className="space-y-6">
      {/* Zephyr image auto-refresh */}
      <ZephyrRefreshCard token={token} />

      {/* Intro card */}
      <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div>
            <h2 className="font-heading text-2xl text-white mb-1">Bulk Catalog CSV Update</h2>
            <p className="text-gray-400 text-sm">
              Push price, stock, and status changes across your whole catalog in one upload.
              SKU is the key — blank cells are ignored so you can leave columns you don't touch.
            </p>
          </div>
          <button
            onClick={downloadTemplate}
            className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm flex items-center gap-2 transition-all"
            data-testid="csv-download-template-btn"
          >
            <Download className="w-4 h-4" /> Download Template
          </button>
        </div>
      </div>

      {/* Upload zone */}
      <div className="bg-[#121212] border-2 border-dashed border-white/15 rounded-sm p-8 text-center">
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={onFileChosen}
          className="hidden"
          data-testid="csv-file-input"
        />
        <Upload className="w-8 h-8 mx-auto text-gray-500 mb-3" />
        <p className="text-white font-semibold mb-1">
          {file ? file.name : "Drop a CSV or click to browse"}
        </p>
        <p className="text-gray-500 text-xs mb-4">
          Required column: <span className="text-[#D4AF37]">sku</span>. Editable: name, category, vendor, online_price, wholesale_price, dealer_price, cost_price, msrp, inventory_count, is_active, image_url.
        </p>
        <div className="flex justify-center gap-3">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="text-xs uppercase tracking-wide font-semibold px-4 py-2 bg-white/5 hover:bg-white/10 text-white rounded-sm border border-white/10 transition-all disabled:opacity-40"
            data-testid="csv-pick-file-btn"
          >
            {file ? "Choose different CSV" : "Choose CSV"}
          </button>
          {file && (
            <button
              onClick={() => runPreview()}
              disabled={busy}
              className="text-xs uppercase tracking-wide font-semibold px-4 py-2 border border-white/20 text-gray-300 hover:border-white hover:text-white rounded-sm transition-all disabled:opacity-40"
              data-testid="csv-preview-btn"
            >
              {busy ? "Analyzing…" : "Re-preview"}
            </button>
          )}
        </div>
      </div>

      {/* Preview + apply */}
      {preview && (
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6" data-testid="csv-preview-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div>
              <h3 className="font-heading text-xl text-white mb-1">
                {applied ? "Applied" : "Preview"}
              </h3>
              <p className="text-gray-400 text-xs">
                Parsed <span className="text-white font-bold">{preview.parsed_rows}</span> rows ·
                <span className="text-[#22c55e] font-bold ml-2">{preview.matched}</span> matched ·
                <span className="text-[#D62828] font-bold ml-2">{preview.not_found}</span> not found ·
                <span className="text-gray-400 ml-2">{preview.unchanged}</span> unchanged
              </p>
            </div>
            {!applied && preview.matched > 0 && (
              <button
                onClick={runApply}
                disabled={busy}
                className="text-xs uppercase tracking-widest font-bold px-4 py-2 bg-[#D62828] hover:bg-[#B91C1C] text-white rounded-sm transition-all disabled:opacity-40"
                data-testid="csv-apply-btn"
              >
                {busy ? "Applying…" : `Apply ${preview.matched} Updates`}
              </button>
            )}
            {applied && (
              <span className="text-[#22c55e] text-xs uppercase tracking-widest font-semibold flex items-center gap-2" data-testid="csv-applied-badge">
                <CheckCircle2 className="w-4 h-4" /> {applied.updated} updates saved
              </span>
            )}
          </div>

          {preview.warnings?.length > 0 && (
            <div className="mb-4 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-sm p-3 text-xs">
              <p className="flex items-center gap-2 text-[#D4AF37] font-semibold mb-1">
                <AlertTriangle className="w-3 h-3" /> {preview.warnings.length} warning{preview.warnings.length === 1 ? '' : 's'}
              </p>
              <ul className="text-gray-400 space-y-0.5 list-disc list-inside">
                {preview.warnings.slice(0, 8).map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}

          <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[#121212]">
                <tr className="text-left text-gray-500 border-b border-white/10">
                  <th className="py-2 pr-3 uppercase tracking-widest">SKU</th>
                  <th className="py-2 pr-3 uppercase tracking-widest">Status</th>
                  <th className="py-2 pr-3 uppercase tracking-widest">Changes</th>
                </tr>
              </thead>
              <tbody>
                {(preview.diffs || []).map((d, i) => (
                  <tr key={`${d.sku}-${i}`} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`csv-diff-row-${d.sku}`}>
                    <td className="py-2 pr-3 text-white font-mono">{d.sku}</td>
                    <td className="py-2 pr-3">
                      {d.status === "will_update" || d.status === "updated" ? (
                        <span className="text-[#22c55e]">{d.status.replace("_", " ")}</span>
                      ) : d.status === "not_found" ? (
                        <span className="text-[#D62828]">not found</span>
                      ) : (
                        <span className="text-gray-500">unchanged</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-gray-300">
                      {d.changes && typeof d.changes === "object" ? (
                        <div className="space-y-1">
                          {Object.entries(d.changes).map(([field, val]) => (
                            <div key={field}>
                              <span className="text-gray-500">{field}:</span>{' '}
                              {val && typeof val === "object" && "from" in val ? (
                                <>
                                  <span className="line-through text-gray-600">{String(val.from ?? "—")}</span>
                                  {' → '}
                                  <span className="text-[#D4AF37]">{String(val.to ?? "—")}</span>
                                </>
                              ) : (
                                <span className="text-[#D4AF37]">{String(val)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!preview && !file && (
        <div className="text-center text-gray-500 text-sm py-8">
          <FileText className="w-8 h-8 mx-auto mb-2 text-gray-700" />
          Choose a CSV to see a diff preview before anything is saved.
        </div>
      )}
    </div>
  );
};

export default CatalogCsvTab;

// ---------- Zephyr image auto-refresh card ----------

const ZephyrRefreshCard = ({ token }) => {
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchLog = async () => {
    try {
      const res = await axios.get(`${API}/admin/jobs/zephyr-image-refresh/log`, authHeaders);
      setRuns(res.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (token) fetchLog(); /* eslint-disable-next-line */ }, [token]);

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/jobs/zephyr-image-refresh`, {}, authHeaders);
      if (res.data?.ok) {
        toast.success(`Refreshed — ${res.data.direct_matches} direct + ${res.data.parent_matches} parent matches`);
      } else {
        toast.error(res.data?.error || "Refresh failed");
      }
      fetchLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    }
    setBusy(false);
  };

  const latest = runs[0];

  return (
    <div className="bg-[#121212] border border-blue-500/30 rounded-sm p-6" data-testid="zephyr-refresh-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-blue-300 text-[10px] uppercase tracking-widest mb-1 flex items-center gap-2">
            <ImageIcon className="w-3 h-3" /> Zephyr Image Auto-Refresh
          </p>
          <h3 className="font-heading text-lg text-white mb-1">Sync Zephyr photos from zephyrpro40.com</h3>
          <p className="text-gray-400 text-xs">
            Runs automatically every week. New Zephyr SKUs get their real product photo matched from the live vendor feed.
          </p>
          {latest && (
            <p className="text-xs mt-2" data-testid="zephyr-refresh-latest">
              {latest.ok ? (
                <span className="text-[#22c55e]">
                  Last run {new Date(latest.ran_at).toLocaleString()} — {latest.direct_matches} direct + {latest.parent_matches} parent = {latest.direct_matches + latest.parent_matches}/{latest.total_zephyr_products} matched
                </span>
              ) : (
                <span className="text-[#D62828]">Last run {new Date(latest.ran_at).toLocaleString()} failed: {latest.error}</span>
              )}
            </p>
          )}
        </div>
        <button
          onClick={runNow}
          disabled={busy}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-sm border border-blue-400/50 text-blue-300 hover:bg-blue-500/10 text-xs uppercase tracking-widest font-semibold transition-all disabled:opacity-40"
          data-testid="zephyr-refresh-run-btn"
        >
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
          {busy ? "Refreshing…" : "Refresh Now"}
        </button>
      </div>

      {runs.length > 1 && (
        <details className="mt-4">
          <summary className="text-[10px] uppercase tracking-widest text-gray-500 cursor-pointer hover:text-white">
            History ({runs.length} run{runs.length === 1 ? '' : 's'})
          </summary>
          <div className="mt-2 space-y-1 text-xs">
            {runs.slice(1, 8).map((r, i) => (
              <div key={i} className="flex justify-between gap-3 py-1 border-b border-white/5" data-testid={`zephyr-run-${i}`}>
                <span className="text-gray-500">{new Date(r.ran_at).toLocaleString()}</span>
                <span className={r.ok ? "text-[#22c55e]" : "text-[#D62828]"}>
                  {r.ok ? `${r.direct_matches + r.parent_matches}/${r.total_zephyr_products} matched` : `failed: ${r.error?.slice(0, 60) || 'unknown'}`}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};
